import React, { useState } from 'react';
import { 
  Modal, 
  Button, 
  Input, 
  Select,
  Space, 
  Tag, 
  message,
  Tooltip,
  Card,
  Typography,
  Divider,
  Form,
  List,
  Alert
} from 'antd';
import { 
  ShareAltOutlined, 
  UserOutlined,
  EditOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { sharesAPI } from '../../services/api';

const { Text } = Typography;
const { Option } = Select;

const BulkShareModal = ({ contacts, visible, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState('write');
  const [form] = Form.useForm();

  const handleBulkShare = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      const contactIds = contacts.map(contact => contact.id);
      const response = await sharesAPI.bulkCreate({
        contact_ids: contactIds,
        email: values.email.trim(),
        permission
      });

      if (response.data.success) {
        const { successful, failed, total } = response.data.data.summary;
        
        if (failed === 0) {
          message.success(`${successful} contact(s) partagé(s) avec succès`);
        } else {
          message.warning(`${successful} contact(s) partagé(s) avec succès, ${failed} échec(s)`);
        }
        
        form.resetFields();
        setEmail('');
        setPermission('write');
        onClose();
      }
    } catch (error) {
      if (error.errorFields) {
        // Erreur de validation du formulaire
        return;
      }
      
      // Gérer les différentes erreurs HTTP
      if (error.response?.status === 400) {
        message.error(error.response?.data?.message || 'Erreur de validation');
      } else if (error.response?.status === 404) {
        message.error('Un ou plusieurs contacts n\'existent pas ou vous n\'avez pas la permission de les partager');
      } else {
        message.error(error.response?.data?.message || 'Erreur lors du partage');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderContactItem = (contact) => (
    <List.Item>
      <List.Item.Meta
        avatar={<UserOutlined />}
        title={`${contact.first_name} ${contact.last_name}`}
        description={
          <div>
            {contact.company && <div><strong>{contact.company}</strong></div>}
            {contact.email && <div>{contact.email}</div>}
          </div>
        }
      />
    </List.Item>
  );

  return (
    <Modal
      title={
        <Space>
          <ShareAltOutlined />
          <span>Partager {contacts.length} contact(s)</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Annuler
        </Button>,
        <Button
          key="share"
          type="primary"
          icon={<ShareAltOutlined />}
          onClick={handleBulkShare}
          loading={loading}
        >
          Partager ({contacts.length})
        </Button>
      ]}
      width={700}
    >
      <Alert
        message={`Vous allez partager ${contacts.length} contact(s) avec un destinataire`}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical">
          <Text strong>Destinataire</Text>
          <Input.Group compact>
            <Form.Item
              name="email"
              noStyle
              rules={[
                { required: true, message: 'Veuillez entrer une adresse email' },
                { type: 'email', message: 'Veuillez entrer une adresse email valide' }
              ]}
            >
              <Input
                placeholder="Email du destinataire"
                prefix={<UserOutlined />}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ width: '60%' }}
              />
            </Form.Item>
            <Form.Item
              name="permission"
              noStyle
              initialValue="write"
            >
              <Select
                value={permission}
                onChange={setPermission}
                style={{ width: '40%' }}
              >
                <Option value="read">
                  <Space>
                    <EyeOutlined />
                    Lecture seule
                  </Space>
                </Option>
                <Option value="write">
                  <Space>
                    <EditOutlined />
                    Écriture
                  </Space>
                </Option>
              </Select>
            </Form.Item>
          </Input.Group>
        </Form>
      </Card>

      <Divider>Contacts à partager</Divider>

      <List
        dataSource={contacts}
        renderItem={renderContactItem}
        size="small"
        style={{ maxHeight: 300, overflow: 'auto' }}
        locale={{
          emptyText: 'Aucun contact sélectionné'
        }}
      />
    </Modal>
  );
};

export default BulkShareModal;
